import React, { useState } from 'react';

import NavItem from '../NavItem';
import { Pin, Phone, Mail, External, ChevronDown } from '_utils/icons';
import { CopyText } from '_utils/components/Corporate';
import { getLink, isValidLinkObject } from '_utils/helpers/link';

import { useTranslation } from 'react-i18next';
import { ProfileProps } from './definitions';
import {
  Group,
  ProfileHeading,
  Role,
  Description,
  DescriptionListWrapper,
  DescriptionTerm,
  DescriptionDetails,
  Link,
  RouteLink,
  Ellipsis,
  Trigger,
  ChevronWrapper
} from './StyledProfiles';

const Profiles: React.FC<ProfileProps> = ({
  id,
  address,
  description,
  email,
  name,
  phoneNumber,
  role,
  website,
  hostName,
  showExpandOption
}) => {
  const [collapsed, setCollapsed] = useState(showExpandOption);
  const descriptionList = [];
  const websiteObject = getLink(website);
  const [t] = useTranslation();

  if (address) {
    descriptionList.push(
      <React.Fragment key="address">
        <DescriptionTerm>
          <Pin title="Address" />
        </DescriptionTerm>
        <DescriptionDetails>{address}</DescriptionDetails>
      </React.Fragment>
    );
  }

  if (phoneNumber) {
    descriptionList.push(
      <React.Fragment key="phone">
        <DescriptionTerm>
          <Phone title={t('accessibility-phone-number')} />
        </DescriptionTerm>
        <DescriptionDetails>
          <Link href={`tel:${phoneNumber.replace(/ /g, '')}`} tabIndex={collapsed ? -1 : null}>
            <Ellipsis>{phoneNumber}</Ellipsis>
          </Link>
        </DescriptionDetails>
      </React.Fragment>
    );
  }

  if (email) {
    descriptionList.push(
      <React.Fragment key="email">
        <DescriptionTerm>
          <Mail title={t('accessibility-email-address')} />
        </DescriptionTerm>
        <DescriptionDetails>
          <Link href={`mailto:${email}`} tabIndex={collapsed ? -1 : null}>
            <Ellipsis>{email}</Ellipsis>
          </Link>
          <CopyText
            toBeCopied={email}
            displayText={t('profile-copy-email-label')}
            success={t('profile-copy-email-success')}
            failed={t('profile-copy-email-failed')}
            tabIndex={collapsed ? -1 : null}
          />
        </DescriptionDetails>
      </React.Fragment>
    );
  }

  if (isValidLinkObject(websiteObject)) {
    descriptionList.push(
      <React.Fragment key="website">
        <DescriptionTerm>
          <External title={t('accessibility-website')} />
        </DescriptionTerm>
        <DescriptionDetails>
          <NavItem StyledLink={RouteLink} link={websiteObject} tabIndex={collapsed ? -1 : null}>
            <Ellipsis>
              {website.text || `${website.linktype === 'internal' ? hostName : ''}${website.href}`}
            </Ellipsis>
          </NavItem>
        </DescriptionDetails>
      </React.Fragment>
    );
  }

  return (
    <Group>
      <ProfileHeading>{name}</ProfileHeading>
      {role && <Role>{role}</Role>}
      {description && <Description>{description}</Description>}

      {descriptionList.length > 0 && (
        <React.Fragment>
          <DescriptionListWrapper id={id} $collapsed={collapsed} aria-hidden={collapsed}>
            {descriptionList}
          </DescriptionListWrapper>
          {showExpandOption && collapsed === true && (
            <Trigger
              type="button"
              onClick={() => setCollapsed(false)}
              aria-label={`${t('profile-accessible-show-details-label-prefix')} ${name}`}
              aria-expanded={!collapsed}
              aria-controls={id}
            >
              {t('profile-show-details-label')}{' '}
              <ChevronWrapper>
                <ChevronDown />
              </ChevronWrapper>
            </Trigger>
          )}
        </React.Fragment>
      )}
    </Group>
  );
};

export default Profiles;
